import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listTruthFacts, mapApiTruthSectionToUi } from '../../../services/truth.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * A record's Truth, by section, for the chip, filters, search and order
 * (CT-5719). Race guard, serialized keys, memoized return; the server narrows.
 */
export function useTruthFacts({ entityType, entityId, quickFilter = 'all', filters, search, orderBy, orderDirection } = {}) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!entityType || !entityId) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
        setStatus(null);
      }
      try {
        const parsedSearch = JSON.parse(searchKey);
        const { body } = await listTruthFacts({
          entityType,
          entityId,
          quickFilter,
          filters: JSON.parse(filtersKey),
          orderBy,
          orderDirection,
          ...(parsedSearch ? { search: parsedSearch } : {}),
        });
        if (requestId !== fetchIdRef.current) return;
        setSections((body?.sections ?? []).map(mapApiTruthSectionToUi));
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setSections([]);
        setStatus(apiErrorStatus(err));
        setError(err?.message ?? 'Failed to load Truth');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId, quickFilter, filtersKey, searchKey, orderBy, orderDirection],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const refresh = useCallback(() => fetchData({ quiet: true }), [fetchData]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ sections, loading, error, accessDenied: status === 403, refetch, refresh }),
    [sections, loading, error, status, refetch, refresh],
  );
}

export default useTruthFacts;
